import React, { useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Tab, Tabs,
  CircularProgress, Alert,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import pageService from '../../services/pageService';
import { parseEmbedUrl } from '../../components/MediaEmbed';

function TabPanel({ children, value, index }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

/**
 * Dialog for inserting media into the page editor.
 * Props:
 *   open     - dialog open state
 *   onClose  - close callback
 *   onInsert - callback(htmlSnippet) with the HTML to insert
 *   courseId  - course ID for uploads
 *   pageId   - page ID (optional, for associating uploads)
 */
function MediaInsertDialog({ open, onClose, onInsert, courseId, pageId }) {
  const [tabIndex, setTabIndex] = useState(0);

  // Upload tab
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Embed tab
  const [embedUrl, setEmbedUrl] = useState('');
  const [embedTitle, setEmbedTitle] = useState('');

  const resetState = useCallback(() => {
    setFile(null);
    setUploading(false);
    setUploadError('');
    setEmbedUrl('');
    setEmbedTitle('');
    setTabIndex(0);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleFileChange = useCallback((e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setUploadError('');
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    setUploadError('');

    try {
      const result = await pageService.uploadMedia(courseId, file, pageId);
      const url = result.url;
      let htmlSnippet = '';

      if (result.media_type === 'video') {
        htmlSnippet = `<video src="${url}" controls aria-label="${file.name}"></video>`;
      } else if (result.media_type === 'audio') {
        htmlSnippet = `<audio src="${url}" controls aria-label="${file.name}"></audio>`;
      } else if (result.media_type === 'image') {
        htmlSnippet = `<img src="${url}" alt="" />`;
      }

      if (onInsert && htmlSnippet) {
        onInsert(htmlSnippet);
      }
      handleClose();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed. Please try again.');
      console.error(err);
    } finally {
      setUploading(false);
    }
  }, [file, courseId, pageId, onInsert, handleClose]);

  const handleEmbed = useCallback(() => {
    const parsedSrc = parseEmbedUrl(embedUrl);
    if (!parsedSrc) {
      setUploadError('Invalid URL. Enter a YouTube or Vimeo URL.');
      return;
    }

    const title = embedTitle.trim() || 'Embedded video';
    const htmlSnippet = `<iframe src="${parsedSrc}" title="${title}"></iframe>`;

    if (onInsert) {
      onInsert(htmlSnippet);
    }
    handleClose();
  }, [embedUrl, embedTitle, onInsert, handleClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="media-dialog-title"
    >
      <DialogTitle id="media-dialog-title">Insert Media</DialogTitle>
      <DialogContent>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          aria-label="Media insertion method"
        >
          <Tab
            icon={<UploadFileIcon />}
            iconPosition="start"
            label="Upload"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            icon={<LinkIcon />}
            iconPosition="start"
            label="Embed URL"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>

        {uploadError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {uploadError}
          </Alert>
        )}

        {/* Upload tab */}
        <TabPanel value={tabIndex} index={0}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Upload a video (MP4, WebM, max 100MB), audio (MP3, OGG, WAV, max
            50MB), or image (JPG, PNG, GIF, max 10MB).
          </Typography>

          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, mb: 2 }}
          >
            {file ? file.name : 'Choose File'}
            <input
              type="file"
              hidden
              accept="video/*,audio/*,image/*"
              onChange={handleFileChange}
            />
          </Button>

          {file && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              {(file.size / (1024 * 1024)).toFixed(1)} MB &middot;{' '}
              {file.type}
            </Typography>
          )}
        </TabPanel>

        {/* Embed tab */}
        <TabPanel value={tabIndex} index={1}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Paste a YouTube or Vimeo URL to embed a video.
          </Typography>
          <TextField
            fullWidth
            label="Video URL"
            value={embedUrl}
            onChange={(e) => setEmbedUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Title (for accessibility)"
            value={embedTitle}
            onChange={(e) => setEmbedTitle(e.target.value)}
            placeholder="Lecture recording — Week 5"
          />
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {tabIndex === 0 ? (
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!file || uploading}
            startIcon={
              uploading ? (
                <CircularProgress size={16} sx={{ color: 'white' }} />
              ) : null
            }
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {uploading ? 'Uploading...' : 'Insert'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleEmbed}
            disabled={!embedUrl}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Insert
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default MediaInsertDialog;
